import React, { useState } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { ContentCopy, Check } from '@mui/icons-material';

/**
 * CopyableTag Component
 * A reusable component that displays a tag ID with hover tooltip and click-to-copy functionality
 * 
 * @param {string} text - The text/tag ID to display
 * @param {string} variant - Display variant: 'chip' or 'text' (default: 'chip')
 * @param {string} color - Chip color (for chip variant only)
 * @param {string} size - Chip size (for chip variant only)
 * @param {object} sx - Additional styles
 */
const CopyableTag = ({ 
  text, 
  variant = 'chip', 
  color = 'default', 
  size = 'small',
  chipVariant = 'filled',
  sx = {} 
}) => {
  const [copied, setCopied] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation(); // Prevent parent click events
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTooltipOpen(true);
      
      // Reset after 2 seconds
      setTimeout(() => {
        setCopied(false);
        setTooltipOpen(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleMouseEnter = () => {
    if (!copied) {
      setTooltipOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!copied) {
      setTooltipOpen(false);
    }
  };

  const tooltipTitle = copied ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Check sx={{ fontSize: 14 }} />
      <span>Copied!</span>
    </Box>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <ContentCopy sx={{ fontSize: 14 }} />
      <span>Click to copy</span>
    </Box>
  );

  if (variant === 'chip') {
    return (
      <Tooltip 
        title={tooltipTitle} 
        open={tooltipOpen}
        arrow
        placement="top"
      >
        <Chip
          label={text}
          size={size}
          color={color}
          variant={chipVariant}
          onClick={handleCopy}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            userSelect: 'text',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: 2,
            },
            ...sx
          }}
        />
      </Tooltip>
    );
  }

  // Text variant
  return (
    <Tooltip 
      title={tooltipTitle} 
      open={tooltipOpen}
      arrow
      placement="top"
    >
      <Typography
        variant="body2"
        onClick={handleCopy}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          cursor: 'pointer',
          display: 'inline-block',
          fontFamily: 'monospace',
          padding: '2px 6px',
          borderRadius: 1,
          backgroundColor: '#f8fafc',
          border: '1px solid #e5e7eb',
          transition: 'all 0.2s ease',
          userSelect: 'text',
          '&:hover': {
            backgroundColor: '#e5e7eb',
            transform: 'scale(1.02)',
            boxShadow: 1,
          },
          ...sx
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};

export default CopyableTag;

